import { convertMvToPercent } from "../../../utils/dataTransforms";
import {
  formatDate,
  formatSpeed,
  formatTemperature,
  formatDecimal,
} from "../../../utils/formatters";
import { logger } from "../../../utils/logger";
import { parseJsonSafe } from "../../../utils/json";

const escapeHtml = (unsafe) => {
  if (unsafe === null || unsafe === undefined) {
    return "";
  }

  return String(unsafe)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

export const createEnhancedPopupContent = ({
  timeCreated,
  geocode,
  battery,
  deviceBrand,
  rawData,
  latitude,
  longitude,
  primaryColor = "#667eea",
  isDarkMode = false,
  title,
  statusColor: _statusColor,
  showSensorGrid = true,
  showCoordinates = true,
}) => {
  let data = parseJsonSafe(rawData || "{}", null);
  if (!data) {
    logger.warn("Error parsing raw data for popup");
    data = {};
  }

  const resolvedTitle =
    title && String(title).trim().length
      ? escapeHtml(title)
      : "📍 Device Location";

  const textColor = isDarkMode ? "#f9fafb" : "#1f2937";
  const panelBg = isDarkMode ? "rgba(255,255,255,0.08)" : "#f9fafb";
  const popupBg = isDarkMode ? "#1f2937" : "#ffffff";

  let content = `
    <style>
      .mapboxgl-popup-content {
        background: ${popupBg} !important;
        color: ${textColor} !important;
        padding: 15px !important;
        border-radius: 8px !important;
      }
      .popup-container { max-width: 300px; font-family: -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif; }
      .popup-header {
        background: ${isDarkMode ? "#374151" : primaryColor};
        color: #fff;
        padding: 12px 16px;
        margin: -15px -15px 12px -15px;
        border-radius: 8px 8px 0 0;
      }
      .popup-title { font-weight: 600; font-size: 14px; margin: 0 0 4px 0; }
      .popup-time { font-size: 12px; opacity: 0.9; margin: 0; }
      .popup-value { font-size: 13px; padding: 8px 12px; background: ${panelBg}; border-radius: 6px; border-left: 3px solid ${primaryColor}; margin-bottom: 8px; }
      .popup-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
      .popup-grid-item { background: ${panelBg}; padding: 8px 10px; border-radius: 6px; border-left: 2px solid ${primaryColor}; }
      .popup-grid-label { font-size: 10px; font-weight: 600; color: ${isDarkMode ? "#9ca3af" : "#6b7280"}; text-transform: uppercase; margin-bottom: 3px; }
      .popup-grid-value { font-size: 13px; color: ${textColor}; font-weight: 500; }
      .coordinates { font-family: 'Courier New', monospace; font-size: 11px; color: ${isDarkMode ? "#9ca3af" : "#6b7280"}; background: ${panelBg}; padding: 8px 10px; border-radius: 6px; margin-top: 8px; }
    </style>
    <div class="popup-container">
      <div class="popup-header">
        <div class="popup-title">${resolvedTitle}</div>
        <div class="popup-time">${formatDate(timeCreated)}</div>
      </div>
  `;

  if (geocode) {
    content += `<div class="popup-value">${escapeHtml(geocode)}</div>`;
  }

  if (showSensorGrid) {
    const entries = [];
    if (battery) {
      entries.push([
        "Battery",
        `🔋 ${convertMvToPercent(battery, deviceBrand)}`,
      ]);
    }
    if (data.speedKmph !== undefined) {
      entries.push(["Speed", `🚗 ${formatSpeed(data.speedKmph)}`]);
    }
    if (data.temperature !== undefined) {
      entries.push([
        "Temperature",
        `🌡️ ${formatTemperature(data.temperature, true)}`,
      ]);
    }
    if (data.humidity_1 !== undefined) {
      entries.push(["Humidity 1", `💧 ${formatDecimal(data.humidity_1)}%`]);
    }
    if (data.altitude !== undefined) {
      entries.push(["Altitude", `⛰️ ${formatDecimal(data.altitude)}m`]);
    }
    if (data.headingDeg !== undefined) {
      entries.push(["Heading", `🧭 ${formatDecimal(data.headingDeg)}°`]);
    }
    if (data.manDown !== undefined) {
      entries.push(["Man Down", `🚨 ${data.manDown ? "Yes" : "No"}`]);
    }

    if (entries.length) {
      content += '<div class="popup-grid">';
      for (const [label, value] of entries) {
        content += `<div class="popup-grid-item"><div class="popup-grid-label">${label}</div><div class="popup-grid-value">${value}</div></div>`;
      }
      content += "</div>";
    }
  }

  if (showCoordinates && latitude && longitude) {
    content += `<div class="coordinates">📍 ${latitude.toFixed(6)}, ${longitude.toFixed(6)}</div>`;
  }

  content += "</div>";
  return content;
};
